import { useEffect, useState } from 'react';
import type { SmsSettingsDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { useT } from '@/i18n/use-t';
import type { MessageKey } from '@/i18n/messages';
import { Section } from '@/components/page';
import { Alert, Button, Field, Input, Pill, Skeleton, Switch } from '@/components/ui';
import { SettingsPage } from '@/pages/settings';

/**
 * Texting and one-time codes (ADR-030).
 *
 * The point of this screen existing before any provider does: which messages go
 * out, what they say, and every OTP rule are decided and enforced now. When a
 * store signs with a gateway, a driver is written and nothing here changes.
 *
 * The transport in use is stated plainly at the top — a store reading "console"
 * knows its messages are being logged, not delivered, which is a fact worth
 * being unmissable.
 */
export function MessagingPage() {
  const t = useT();

  const [config, setConfig] = useState<SmsSettingsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, { ar: string; en: string }>>({});

  useEffect(() => {
    void withSession(() => adminApi.sms.config())
      .then(setConfig)
      .catch((cause) => setError(errorMessage(cause, 'Could not load the messaging settings.')))
      .finally(() => setLoading(false));
  }, []);

  const save = async (patch: Parameters<typeof adminApi.sms.update>[0]) => {
    setSaving(true);
    setError(null);

    try {
      setConfig(await withSession(() => adminApi.sms.update(patch)));
    } catch (cause) {
      setError(errorMessage(cause, 'Could not save.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return (
      <SettingsPage title="messaging" subtitle="settingsMessagingHint">
        {loading ? <Skeleton className="h-64" /> : error ? <Alert>{error}</Alert> : null}
      </SettingsPage>
    );
  }

  return (
    <SettingsPage title="messaging" subtitle="settingsMessagingHint">
      {error ? <Alert>{error}</Alert> : null}

      <Section
        title={t('smsDelivery')}
        subtitle={t('smsDeliveryHint')}
        action={
          <>
            <Pill tone={config.driver === 'console' ? 'warning' : 'success'}>
              {config.driver === 'console' ? t('smsConsoleDriver') : config.driver}
            </Pill>
            <Switch
              label={t('smsDelivery')}
              checked={config.enabled}
              disabled={saving}
              onChange={(enabled) => void save({ enabled })}
            />
          </>
        }
      >
        <div className="max-w-sm">
          <Field label={t('senderName')} htmlFor="sender" hint={t('senderNameHint')}>
            <Input
              id="sender"
              dir="ltr"
              defaultValue={config.senderName}
              maxLength={20}
              onBlur={(event) => {
                if (event.target.value !== config.senderName) {
                  void save({ senderName: event.target.value });
                }
              }}
            />
          </Field>
        </div>
      </Section>

      <Section
        title={t('otpSignIn')}
        subtitle={t('otpSignInHint')}
        action={
          <Switch
            label={t('otpSignIn')}
            checked={config.otp.enabled}
            disabled={saving}
            onChange={(enabled) => void save({ otp: { enabled } })}
          />
        }
      >
        {config.otp.enabled && !config.enabled ? (
          /* Both switches matter: OTP rides on the same transport. */
          <div className="mb-4">
            <Alert>{t('otpNeedsSms')}</Alert>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          {(
            [
              ['length', 4, 8],
              ['ttlSeconds', 60, 900],
              ['resendSeconds', 15, 600],
              ['maxPerHour', 1, 20],
              ['maxAttempts', 1, 10],
            ] as const
          ).map(([key, min, max]) => (
            <Field
              key={key}
              label={t(`otp${key}` as MessageKey)}
              htmlFor={`otp-${key}`}
              hint={`${min}–${max}`}
            >
              <Input
                id={`otp-${key}`}
                type="number"
                dir="ltr"
                min={min}
                max={max}
                defaultValue={config.otp[key]}
                onBlur={(event) => {
                  const value = Number(event.target.value);
                  if (value !== config.otp[key]) void save({ otp: { [key]: value } });
                }}
              />
            </Field>
          ))}
        </div>
      </Section>

      <Section title={t('smsEvents')} subtitle={t('smsEventsHint')} flush>
        <ul className="flex flex-col border-t border-line">
          {config.events.map((event) => {
            const draft = drafts[event.key] ?? event.template;

            return (
              <li key={event.key} className="border-t border-line px-5 py-4 first:border-t-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span dir="ltr" className="font-mono text-xs text-ink">
                    {event.key}
                  </span>
                  {event.customised ? <Pill tone="info">{t('customised')}</Pill> : null}
                  {event.key === 'otp.login' ? <Pill tone="neutral">{t('alwaysSent')}</Pill> : null}

                  <span className="ms-auto">
                    <Switch
                      label={`${t('enabled')} — ${event.key}`}
                      checked={event.enabled}
                      disabled={saving || event.key === 'otp.login'}
                      onChange={(next) => void save({ events: { [event.key]: next } })}
                    />
                  </span>
                </div>

                <p className="mt-2 text-xs text-ink-muted">
                  {t('placeholders')}: <span dir="ltr">{event.placeholders.map((name) => `{${name}}`).join(' ')}</span>
                </p>

                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {(['ar', 'en'] as const).map((language) => (
                    <Field
                      key={language}
                      label={t(language === 'ar' ? 'arabic' : 'english')}
                      htmlFor={`${event.key}-${language}`}
                    >
                      <Input
                        id={`${event.key}-${language}`}
                        dir={language === 'ar' ? 'rtl' : 'ltr'}
                        value={draft[language]}
                        onChange={(change) =>
                          setDrafts((current) => ({
                            ...current,
                            [event.key]: { ...draft, [language]: change.target.value },
                          }))
                        }
                      />
                    </Field>
                  ))}
                </div>

                {drafts[event.key] ? (
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      disabled={saving}
                      onClick={async () => {
                        await save({ templates: { [event.key]: drafts[event.key] } });
                        setDrafts((current) => {
                          const next = { ...current };
                          delete next[event.key];
                          return next;
                        });
                      }}
                    >
                      {saving ? t('saving') : t('save')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setDrafts((current) => {
                          const next = { ...current };
                          delete next[event.key];
                          return next;
                        })
                      }
                    >
                      {t('cancel')}
                    </Button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </Section>
    </SettingsPage>
  );
}
